import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, Tooltip } from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { useNotifications, Notificacao, TipoNotificacao } from "../../contexts/NotificationContext.tsx";
import { formatarTempoRelativo } from "../../utils/relativeTime.util.ts";

const ICONES_POR_TIPO: Record<TipoNotificacao, { icon: React.ReactNode; bg: string; fg: string }> = {
  cirurgia_agendada: { icon: <EventAvailableOutlinedIcon fontSize="small" />, bg: "var(--blue-tint)", fg: "var(--blue)" },
  cirurgia_cancelada: { icon: <EventBusyOutlinedIcon fontSize="small" />, bg: "var(--red-tint)", fg: "var(--red)" },
  cirurgia_realizada: { icon: <CheckCircleOutlineIcon fontSize="small" />, bg: "var(--green-tint)", fg: "var(--green)" },
  cirurgia_status_alterado: { icon: <SwapHorizOutlinedIcon fontSize="small" />, bg: "var(--blue-tint)", fg: "var(--blue)" },
  cadastro_medico: { icon: <MedicalServicesOutlinedIcon fontSize="small" />, bg: "var(--teal-tint)", fg: "var(--teal)" },
  cadastro_paciente: { icon: <PersonAddAltOutlinedIcon fontSize="small" />, bg: "var(--teal-tint)", fg: "var(--teal)" },
  cadastro_instrumentador: { icon: <BadgeOutlinedIcon fontSize="small" />, bg: "var(--teal-tint)", fg: "var(--teal)" },
  sistema: { icon: <CampaignOutlinedIcon fontSize="small" />, bg: "var(--surface-3)", fg: "var(--ink-soft)" },
};

// Popover do MUI renderiza num Portal fora da árvore do componente — CSS
// escopado (.ds-*) não alcança, precisa estilizar aqui também (mesmo padrão
// já usado nos outros popups/menus dessa sessão).
const popoverSlotProps = {
  paper: {
    sx: {
      width: 380,
      maxWidth: "94vw",
      bgcolor: "var(--surface)",
      color: "var(--ink)",
      border: "1px solid var(--border)",
      borderRadius: "14px",
      boxShadow: "var(--shadow-lg)",
      mt: 1,
      overflow: "hidden",
    },
  },
};

const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const { notificacoes, naoLidas, carregando, marcarComoLida, marcarTodasComoLidas, excluirNotificacao, limparTodas } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const abrir = (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget);
  // Fechar o painel (clique fora, Esc, clicar numa notificação, etc.) marca
  // tudo que estava visível como lido — evita o badge vermelho continuar
  // preso depois que o usuário já viu a lista, sem precisar clicar em
  // "Marcar todas como lidas" manualmente toda vez.
  const fechar = () => {
    setAnchorEl(null);
    if (naoLidas > 0) marcarTodasComoLidas();
  };

  const handleClicarNotificacao = (n: Notificacao) => {
    if (!n.lida) marcarComoLida(n.id);
    fechar();
    if (n.link) navigate(n.link);
  };

  const handleExcluirNotificacao = (e: React.MouseEvent, id: string) => {
    // Não deixa o clique "vazar" pro card (que marcaria como lida e navegaria).
    e.stopPropagation();
    excluirNotificacao(id);
  };

  return (
    <>
      <Tooltip title="Notificações">
        <button type="button" className="ds-icon-btn" onClick={abrir} aria-label="Notificações">
          <NotificationsNoneOutlinedIcon fontSize="small" />
          {naoLidas > 0 && <span className="badge-count">{naoLidas > 99 ? "99+" : naoLidas}</span>}
        </button>
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={fechar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={popoverSlotProps}
      >
        <div className="ds-notif-header">
          <div className="ds-notif-header-row">
            <h4>Notificações</h4>
            <button
              type="button"
              className="ds-notif-clear-all"
              onClick={limparTodas}
              disabled={notificacoes.length === 0}
              title="Limpar todas as notificações"
            >
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              Limpar todas
            </button>
          </div>
          <div className="ds-notif-header-row ds-notif-header-row-sub">
            {naoLidas > 0 && (
              <span className="ds-notif-count-chip">
                {naoLidas} nova{naoLidas === 1 ? "" : "s"}
              </span>
            )}
            <button
              type="button"
              className="ds-notif-mark-all"
              onClick={marcarTodasComoLidas}
              disabled={naoLidas === 0}
            >
              Marcar todas como lidas
            </button>
          </div>
        </div>

        <div className="ds-notif-list">
          {carregando ? (
            <div className="ds-notif-empty">Carregando notificações…</div>
          ) : notificacoes.length === 0 ? (
            <div className="ds-notif-empty">Nenhuma notificação no momento.</div>
          ) : (
            notificacoes.map((n) => {
              const cfg = ICONES_POR_TIPO[n.tipo];
              return (
                <div
                  key={n.id}
                  className={`ds-notif-item ${!n.lida ? "unread" : ""}`}
                  onClick={() => handleClicarNotificacao(n)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ds-notif-icon" style={{ background: cfg.bg, color: cfg.fg }}>
                    {cfg.icon}
                  </div>
                  <div className="ds-notif-body">
                    <div className="title">{n.titulo}</div>
                    <div className="desc">{n.descricao}</div>
                    {n.timestamp && <div className="time">{formatarTempoRelativo(n.timestamp)}</div>}
                  </div>
                  {!n.lida && <span className="ds-notif-unread-dot" />}
                  <button
                    type="button"
                    className="ds-notif-delete-btn"
                    onClick={(e) => handleExcluirNotificacao(e, n.id)}
                    aria-label="Excluir notificação"
                    title="Excluir notificação"
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </Popover>
    </>
  );
};

export default NotificationCenter;
